import React, { useCallback, useRef, useState } from "react";
import { FaFolder } from "react-icons/fa";
import { ResourceType } from "@/models/resource-group";

// ============================================================================
// Types
// ============================================================================

/** Drop mode: 'reorder' for moving between items, 'group' for creating a group */
export type DropMode = "reorder" | "group";

export interface ResourceItemWrapperProps {
  /** Unique identifier of the resource */
  resourceId: string;
  /** Type of resource for drag data */
  resourceType: ResourceType;
  /** Display name for drag preview */
  displayName: string;
  /** Children to render (the actual item component) */
  children: React.ReactNode;
  /** Whether the item is draggable */
  draggable?: boolean;
  /** Callback when drag starts */
  onDragStart?: (e: React.DragEvent, resourceId: string) => void;
  /** Callback when dragging over this item */
  onDragOver?: (
    e: React.DragEvent,
    resourceId: string,
    dropMode: DropMode,
  ) => void;
  /** Callback when drag leaves this item */
  onDragLeave?: (e: React.DragEvent) => void;
  /** Callback when dropping on this item (for creating groups) */
  onDrop?: (e: React.DragEvent, resourceId: string, dropMode: DropMode) => void;
  /** Whether this item is being dragged */
  isDragging?: boolean;
  /** Whether something is being dragged over this item */
  isDragOver?: boolean;
  /** Current drop mode */
  dropMode?: DropMode;
}

// Data transfer type for resource drag & drop (for grouping resources)
export const RESOURCE_DRAG_TYPE = "application/x-resource-item";

/**
 * Resource drag payload interface.
 */
export interface ResourceDragPayload {
  type: "resource";
  resourceId: string;
  resourceType: ResourceType;
  displayName: string;
}

/**
 * Creates a drag payload for a resource item.
 */
export function createResourceDragPayload(
  resourceId: string,
  resourceType: ResourceType,
  displayName: string,
): ResourceDragPayload {
  return {
    type: "resource",
    resourceId,
    resourceType,
    displayName,
  };
}

/**
 * Sets the resource drag payload on the drag event.
 */
export function setResourceDragData(
  event: React.DragEvent,
  resourceId: string,
  resourceType: ResourceType,
  displayName: string,
): void {
  const payload = createResourceDragPayload(
    resourceId,
    resourceType,
    displayName,
  );
  // Use only text/plain to avoid browser compatibility issues
  event.dataTransfer.setData("text/plain", JSON.stringify(payload));
  event.dataTransfer.effectAllowed = "move";
}

/**
 * Parses a resource drag payload from the event.
 */
export function parseResourceDragPayload(
  data: string,
): ResourceDragPayload | null {
  try {
    const parsed = JSON.parse(data);
    if (parsed.type === "resource") {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Gets the resource drag payload from a drag event.
 */
export function getResourceDragPayload(
  event: React.DragEvent,
): ResourceDragPayload | null {
  // First try the custom type (for backward compatibility)
  let data = event.dataTransfer.getData(RESOURCE_DRAG_TYPE);
  if (!data) {
    // Fallback to text/plain
    data = event.dataTransfer.getData("text/plain");
  }
  if (!data) return null;
  return parseResourceDragPayload(data);
}

/**
 * Checks if the drag event contains a resource item.
 */
export function hasResourceDragData(event: React.DragEvent): boolean {
  // Always return true for now since we use text/plain
  // We'll validate the actual content when we get it
  return event.dataTransfer.types.includes("text/plain");
}

// ============================================================================
// Component
// ============================================================================

/**
 * ResourceItemWrapper component - wraps a resource item to add drag-and-drop functionality.
 * Supports dragging resources to groups and creating groups by dropping on other items.
 *
 * This is a generic wrapper that works with any resource type (tracker, enrichment, alert).
 */
const ResourceItemWrapper: React.FC<ResourceItemWrapperProps> = ({
  resourceId,
  resourceType,
  displayName,
  children,
  draggable = true,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragging = false,
  isDragOver = false,
  dropMode = "reorder",
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const [localDragOver, setLocalDragOver] = useState(false);
  const [localDropMode, setLocalDropMode] = useState<DropMode>("reorder");

  // Determine drop mode based on cursor position within the element
  // Center zone (30%-70% of height) = group mode, edges = reorder mode
  const getDropMode = useCallback((e: React.DragEvent): DropMode => {
    if (!itemRef.current) return "reorder";
    const rect = itemRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const relativeY = y / rect.height;
    // Center zone (30%-70%) triggers group mode
    return relativeY > 0.3 && relativeY < 0.7 ? "group" : "reorder";
  }, []);

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = "copyMove";
      e.dataTransfer.setData("text/plain", resourceId);
      const payload = createResourceDragPayload(
        resourceId,
        resourceType,
        displayName,
      );
      e.dataTransfer.setData(RESOURCE_DRAG_TYPE, JSON.stringify(payload));

      // Create visual drag preview
      if (itemRef.current && e.dataTransfer.setDragImage) {
        const rect = itemRef.current.getBoundingClientRect();
        // Scale down to a small preview (max 200px width)
        const maxWidth = 200;
        const scale = Math.min(1, maxWidth / rect.width);
        const scaledWidth = rect.width * scale;
        const scaledHeight = rect.height * scale;

        // Create a wrapper that has the final scaled size
        const wrapper = document.createElement("div");
        wrapper.style.position = "absolute";
        wrapper.style.top = "-9999px";
        wrapper.style.left = "-9999px";
        wrapper.style.width = `${scaledWidth}px`;
        wrapper.style.height = `${scaledHeight}px`;
        wrapper.style.overflow = "hidden";
        wrapper.style.borderRadius = "8px";
        wrapper.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
        wrapper.style.border = "2px solid var(--arc-accent)";

        // Create a scaled clone inside the wrapper
        const clone = itemRef.current.cloneNode(true) as HTMLElement;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.transform = `scale(${scale})`;
        clone.style.transformOrigin = "top left";
        clone.style.opacity = "0.95";
        clone.style.background = "var(--arc-sidebar-bg)";

        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);

        // Set the drag image with offset to center it
        e.dataTransfer.setDragImage(wrapper, scaledWidth / 2, scaledHeight / 2);

        // Remove the wrapper after a short delay
        requestAnimationFrame(() => {
          document.body.removeChild(wrapper);
        });
      }

      onDragStart?.(e, resourceId);
    },
    [resourceId, resourceType, displayName, onDragStart],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const mode = getDropMode(e);
      setLocalDragOver(true);
      setLocalDropMode(mode);
      onDragOver?.(e, resourceId, mode);
    },
    [getDropMode, onDragOver, resourceId],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      setLocalDragOver(false);
      setLocalDropMode("reorder");
      onDragLeave?.(e);
    },
    [onDragLeave],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const mode = getDropMode(e);
      setLocalDragOver(false);
      setLocalDropMode("reorder");
      onDrop?.(e, resourceId, mode);
    },
    [getDropMode, onDrop, resourceId],
  );

  // Use external or local state for visual indicators
  const effectiveDragOver = isDragOver || localDragOver;
  const effectiveDropMode = dropMode !== "reorder" ? dropMode : localDropMode;

  // Visual indicators based on drop mode
  const isGroupMode = effectiveDragOver && effectiveDropMode === "group";
  const isReorderMode = effectiveDragOver && effectiveDropMode === "reorder";

  return (
    <div
      ref={itemRef}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative ${isDragging ? "opacity-50" : ""} ${
        isReorderMode ? "border-t-2 border-t-[var(--arc-accent)]" : ""
      } ${isGroupMode ? "bg-[var(--arc-accent)]/20 ring-2 ring-[var(--arc-accent)] ring-inset rounded-lg" : ""}`}
    >
      {/* Group mode indicator - folder icon overlay */}
      {isGroupMode && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="bg-[var(--arc-sidebar-bg)] rounded-full p-1.5 shadow-lg">
            <FaFolder
              className="text-[var(--arc-accent)] animate-pulse"
              size={16}
            />
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

ResourceItemWrapper.displayName = "ResourceItemWrapper";

export default ResourceItemWrapper;
